/* eslint-disable no-console */
/**
 * 🔐 Logger conditionnel pour production
 * 
 * SEC-012: Supprime les logs de debug en production
 * pour éviter les fuites d'informations.
 * 
 * Usage:
 * import { logger } from '@/utils/logger';
 * logger.debug('Info de debug'); // Uniquement en dev
 * logger.error('Erreur critique'); // Toujours affiché
 */

const isDev = import.meta.env.DEV;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  /** Log uniquement en dev, avec un tag personnalisé */
  devOnly: (tag: string, ...args: unknown[]) => void;
}

/**
 * Logger avec niveaux de visibilité
 * - debug/info: Uniquement en développement
 * - warn/error: Toujours affichés (erreurs à corriger)
 */
export const logger: Logger = {
  /**
   * Log de debug - UNIQUEMENT en développement
   * Utilisé pour le débogage détaillé
   */
  debug: (...args: unknown[]) => {
    if (isDev) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Log d'information - UNIQUEMENT en développement
   * Utilisé pour le suivi d'exécution normal
   */
  info: (...args: unknown[]) => {
    if (isDev) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Log d'avertissement - TOUJOURS affiché
   * Utilisé pour les situations potentiellement problématiques
   */
  warn: (...args: unknown[]) => {
    console.warn('[WARN]', ...args);
  },

  /**
   * Log d'erreur - TOUJOURS affiché
   * Utilisé pour les erreurs à corriger
   */
  error: (...args: unknown[]) => {
    console.error('[ERROR]', ...args);
  },

  /**
   * Log personnalisé - UNIQUEMENT en développement
   * @param tag - Tag pour identifier la source (ex: '🔐 Auth', '📊 Data')
   */
  devOnly: (tag: string, ...args: unknown[]) => {
    if (isDev) {
      console.log(`[${tag}]`, ...args);
    }
  }
};

export default logger;
